import { addToast } from "@heroui/react";
import { useShallow } from "zustand/shallow";
import { useEffect, useState } from "react";

import { TagInput } from "../../../tags/components/TagInput";
import { TagsList } from "../../../tags/components/TagsList";
import { useBoundStore } from "@/app/store";
import { FetchedNote } from "@/services/api/fetchers/notes";

type FetchedTag = NonNullable<FetchedNote["tags"]>[number];

interface NoteTagsProps {
  noteId: number;
  onChange?: (newTags: FetchedTag[]) => void;
}

export function SetNoteTagsOption({ noteId, onChange }: NoteTagsProps) {
  const [filterText, setFilterText] = useState("");

  const { authUserId, notes, tags, addTag, addNoteTag, removeNoteTag } = useBoundStore(
    useShallow((state) => ({
      authUserId: state.user.id,
      notes: state.notes.main,
      tags: state.tags.list,
      addTag: state.tags.addTag,
      addNoteTag: state.tags.addNoteTag,
      removeNoteTag: state.tags.removeNoteTag,
    }))
  );
  const note = notes.find((note) => note.id === noteId);
  const noteTags = note?.tags;

  useEffect(() => {
    if (onChange && noteTags !== undefined) {
      onChange(noteTags);
    }
  }, [noteTags, onChange]);

  const selectedTags = noteTags?.map((tag) => tag.id.toString()) || [];

  const handleCheckboxChange = (selectedTagsIds: string[]) => {
    const addedTagId = selectedTagsIds.find((tagId) => !selectedTags.includes(tagId));
    if (addedTagId) {
      addNoteTag(noteId, parseInt(addedTagId));
      return;
    }

    const removedTagId = selectedTags.find((tagId) => !selectedTagsIds.includes(tagId));
    if (removedTagId) {
      removeNoteTag(noteId, parseInt(removedTagId));
    }
  };

  const handleAddTag = (tagName: string) => {
    if (!tagName || !authUserId) return;

    const existingTag = tags.find((tag) => tag.name === tagName);
    if (existingTag) {
      // If tag exists and is not already selected, add it to the note
      if (!selectedTags.includes(existingTag.id.toString())) {
        addNoteTag(noteId, existingTag.id);
        setFilterText(""); // Clear the filter after adding the tag
        return;
      }
      addToast({
        description: "This tag is already added to the note.",
        title: "Tag already selected",
      });
      return;
    }

    addTag({ name: tagName, user_id: authUserId });
    setFilterText(""); // Clear the filter after adding a new tag
  };

  return (
    <div className="w-full p-1">
      <TagInput className="mb-4" onAddTag={handleAddTag} onValueChange={setFilterText} />
      <TagsList
        tags={tags}
        selectedTags={selectedTags}
        onCheckboxChange={handleCheckboxChange}
        filterText={filterText}
      />
    </div>
  );
}
